import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import PaymentForm from './components/PaymentForm';
import BookingSummary from './components/BookingSummary';
import SecurityBadges from './components/SecurityBadges';
import ConfirmationModal from './components/ConfirmationModal';

const PaymentCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [bookingData, setBookingData] = useState({
    tourId: '',
    tourName: 'Amazing City Tour',
    selectedDate: '',
    adults: 2,
    children: 0,
    basePrice: 299,
    addOns: [],
    totalAmount: 598,
    bookingId: '',
    status: 'pending'
  });
  
  const [paymentData, setPaymentData] = useState({
    paymentMethod: '',
    billingAddress: {
      fullName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    cardInfo: {
      cardNumber: '',
      expiryDate: '',
      cvv: '',
      cardholderName: ''
    },
    guestCheckout: true,
    createAccount: false,
    savePaymentInfo: false,
    acceptTerms: false,
    subscribeNewsletter: false
  });
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errors, setErrors] = useState({});
  const [sessionExpiry, setSessionExpiry] = useState(null);

  // Initialize data from booking flow or localStorage
  useEffect(() => {
    const searchParams = new URLSearchParams(location?.search);
    const savedBookingData = localStorage.getItem('bookingData');
    const savedPaymentData = localStorage.getItem('paymentData');
    
    if (savedBookingData) {
      try {
        const parsed = JSON.parse(savedBookingData);
        setBookingData(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error('Error loading booking data:', error);
      }
    }
    
    if (savedPaymentData) {
      try {
        const parsed = JSON.parse(savedPaymentData);
        setPaymentData(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error('Error loading payment data:', error);
      }
    }
    
    // Set session expiry timer (15 minutes)
    const expiry = Date.now() + (15 * 60 * 1000);
    setSessionExpiry(expiry);
  }, [location?.search]);

  // Save payment data to localStorage
  useEffect(() => {
    const dataToSave = { ...paymentData };
    // Don't save sensitive card information
    delete dataToSave?.cardInfo;
    localStorage.setItem('paymentData', JSON.stringify(dataToSave));
  }, [paymentData]);

  // Session timeout handler
  useEffect(() => {
    if (!sessionExpiry) return;
    
    const timer = setInterval(() => {
      if (Date.now() >= sessionExpiry) {
        alert('Your session has expired. Please start over.');
        navigate('/booking-flow');
      }
    }, 60000); // Check every minute
    
    return () => clearInterval(timer);
  }, [sessionExpiry, navigate]);

  // Update page title
  useEffect(() => {
    document.title = 'Payment & Checkout - TourEase';
  }, []);

  const handlePaymentSubmit = async (formData) => {
    setIsProcessing(true);
    setErrors({});

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Generate booking confirmation
      const bookingId = `TE${Date.now()}`;
      
      const updatedBookingData = {
        ...bookingData,
        bookingId,
        status: 'confirmed',
        paymentMethod: formData?.paymentMethod,
        paymentDate: new Date()?.toISOString(),
        confirmationNumber: bookingId
      };
      
      setBookingData(updatedBookingData);
      localStorage.setItem('confirmedBooking', JSON.stringify(updatedBookingData));
      localStorage.removeItem('bookingData');
      localStorage.removeItem('paymentData');
      
      setShowConfirmation(true);
      
    } catch (error) {
      console.error('Payment processing error:', error);
      setErrors({ submit: 'Payment processing failed. Please try again.' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBackToBooking = () => {
    navigate('/booking-flow');
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    navigate('/user-dashboard');
  };

  const remainingTime = sessionExpiry ? Math.max(0, Math.ceil((sessionExpiry - Date.now()) / 60000)) : 15;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Progress & Security Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBackToBooking}
                className="text-blue-600 hover:text-blue-700 flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Back to Booking</span>
              </button>
              <div className="hidden sm:block text-sm text-gray-500">
                Session expires in {remainingTime} minutes
              </div>
            </div>
            <SecurityBadges />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-x-12">
          {/* Payment Form - Left Column */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-8">
                <div className="mb-8">
                  <h1 className="text-3xl font-bold text-gray-900">Complete Your Payment</h1>
                  <p className="mt-2 text-gray-600">
                    Secure checkout powered by industry-leading encryption
                  </p>
                </div>

                <PaymentForm
                  paymentData={paymentData}
                  setPaymentData={setPaymentData}
                  onSubmit={handlePaymentSubmit}
                  isProcessing={isProcessing}
                  errors={errors}
                  bookingData={bookingData}
                />
              </div>
            </div>
          </div>

          {/* Booking Summary - Right Column */}
          <div className="lg:col-span-5 mt-8 lg:mt-0">
            <BookingSummary
              bookingData={bookingData}
              paymentData={paymentData}
            />
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <ConfirmationModal
          bookingData={bookingData}
          onClose={handleConfirmationClose}
        />
      )}
    </div>
  );
};

export default PaymentCheckout;